import { useState, type FormEvent } from 'react';
import { aboutData } from '@/mocks/about';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d90kbgc6ci2plld1oeag';

export default function ContactSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;

    // Honeypot check
    const honeypot = form.querySelector<HTMLInputElement>('input[name="phone_alt"]');
    if (honeypot && honeypot.value.trim() !== '') {
      setStatus('success');
      form.reset();
      setTimeout(() => setStatus('idle'), 3000);
      return;
    }

    // Textarea length check
    const message = form.querySelector<HTMLTextAreaElement>('textarea[name="message"]');
    if (message && message.value.length > 500) {
      setStatus('error');
      setErrorMsg('留言内容不能超过500个字符');
      return;
    }

    setStatus('submitting');

    try {
      const formData = new FormData(form);
      const urlEncoded = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt') {
          urlEncoded.append(key, value.toString());
        }
      });

      const res = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: urlEncoded.toString(),
      });

      if (res.ok) {
        setStatus('success');
        form.reset();
        setTimeout(() => setStatus('idle'), 3000);
      } else {
        throw new Error('提交失败');
      }
    } catch {
      setStatus('error');
      setErrorMsg('提交失败，请稍后重试');
    }
  };

  return (
    <section className="relative w-full overflow-hidden" id="contact">
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=Warm%20abstract%20flowing%20gradient%20soft%20golden%20and%20amber%20tones%20modern%20minimal%20design%20gentle%20curves%20elegant%20atmosphere%20clean%20aesthetic%20harmonious%20composition%20subtle%20texture&width=1920&height=800&seq=about-cta-bg&orientation=landscape"
          alt=""
          className="w-full h-full object-cover object-top"
        />
      </div>
      <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/35 to-black/50"></div>

      <div className="relative z-10 py-14 md:py-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-start">
            {/* Left: Contact Info */}
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-background-50">
                联系我们
              </h2>
              <p className="mt-4 text-sm md:text-base text-background-50/70 leading-relaxed max-w-md">
                有爆料线索？有合作意向？或者只是想和我们聊聊天？欢迎通过以下方式联系我们，或者直接填写右侧表单，我们会尽快回复。
              </p>

              <div className="mt-8 space-y-5">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-background-50/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-background-50 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-background-50/50">电子邮箱</div>
                    <div className="text-sm text-background-50 font-medium">{aboutData.contactInfo.email}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-background-50/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-wechat-line text-background-50 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-background-50/50">微信公众号</div>
                    <div className="text-sm text-background-50 font-medium">{aboutData.contactInfo.wechat}</div>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 rounded-xl bg-background-50/15 flex items-center justify-center flex-shrink-0">
                    <i className="ri-weibo-line text-background-50 text-lg w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <div>
                    <div className="text-xs text-background-50/50">微博</div>
                    <div className="text-sm text-background-50 font-medium">{aboutData.contactInfo.weibo}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Right: Contact Form */}
            <div className="bg-background-50 rounded-2xl p-6 md:p-8">
              <h3 className="text-lg font-heading font-bold text-foreground-900 mb-6">
                发送消息
              </h3>

              {status === 'success' && (
                <div className="mb-6 p-4 rounded-xl bg-accent-100 text-accent-800 text-sm font-medium">
                  消息已成功发送！我们会尽快回复您。
                </div>
              )}

              {status === 'error' && (
                <div className="mb-6 p-4 rounded-xl bg-primary-100 text-primary-700 text-sm font-medium">
                  {errorMsg}
                </div>
              )}

              <form
                onSubmit={handleSubmit}
                data-readdy-form
                className="space-y-4"
              >
                {/* Honeypot */}
                <div className="website-alt-wrapper">
                  <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="contact-name" className="block text-xs font-medium text-foreground-500 mb-1.5">
                      姓名
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      type="text"
                      required
                      placeholder="你的名字"
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-xs font-medium text-foreground-500 mb-1.5">
                      邮箱
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      required
                      placeholder="your@email.com"
                      className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="contact-subject" className="block text-xs font-medium text-foreground-500 mb-1.5">
                    主题
                  </label>
                  <input
                    id="contact-subject"
                    name="subject"
                    type="text"
                    required
                    placeholder="你想聊什么？"
                    className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  />
                </div>

                <div>
                  <label htmlFor="contact-message" className="block text-xs font-medium text-foreground-500 mb-1.5">
                    留言内容
                    <span className="text-foreground-300 ml-1">（最多500字）</span>
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    required
                    rows={5}
                    maxLength={500}
                    placeholder="写下你想说的话..."
                    className="w-full px-4 py-2.5 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  className="w-full py-3 rounded-xl bg-primary-500 text-background-50 font-bold text-sm hover:bg-primary-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors cursor-pointer whitespace-nowrap"
                >
                  {status === 'submitting' ? '发送中...' : '发送消息'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}